/* eslint-disable react/button-has-type */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Fragment, useRef } from "react";
import { Link } from "react-router-dom";
import { FabProps, Tooltip } from "@material-ui/core";
import noop from "lodash/fp/noop";
import _get from "lodash/get";
import { DEFAULT_COLORS } from "src/config/constants";
import { applyCss } from "src/utils/emotion";
import {
  buttonCss,
  buttonWrapperCss,
  circularBtnCss,
  disabledBtnCss,
  extendedBtnCss,
  FunctionButtonIcon,
  FunctionButtonLoader,
  IconContainer,
  linkCss,
} from "./styles";
import {
  ButtonClickCallback,
  ButtonType,
  IconName,
  LinkTo,
} from "../../../../../globalTypes";
import { Variant } from "./types";
import { useTheme } from "../../../../../config/emotion";
import { Colors } from "../../../../../globalTypes/theme";

const evaluateButtonColor = (
  buttonColor: string | undefined,
  colors: Colors
): string => {
  if (buttonColor === undefined) return DEFAULT_COLORS.secondaryColor;
  const colorFromTheme = _get(colors, buttonColor);
  if (typeof colorFromTheme === "string") return colorFromTheme;
  return buttonColor;
};

const EwaButton2 = ({
  buttonColor,
  label,
  iconName,
  onClick = noop,
  isLoading = false,
  loaderSize = 25,
  disabled,
  type = ButtonType.Button,
  className,
  variant,
  tooltip,
  iconSize = 21,
  linkTo,
  dataCy,
  ...rest // ostatni Fab props
}: EwaButton2Props) => {
  const { colors } = useTheme();
  const isDisabled = disabled || isLoading;
  const inputRef = useRef<HTMLButtonElement>(null);

  const handleFocus = () => {
    if (inputRef.current)
      inputRef.current.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          inputRef.current?.click();
        }
      });
  };

  const handleBlur = () => {
    if (inputRef.current)
      inputRef.current.removeEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          inputRef.current?.click();
        }
      });
  };

  const renderStyledFunctionButton = () => (
    <button
      key="ewa-button"
      onClick={onClick}
      disabled={isDisabled}
      ref={inputRef}
      type={type}
      css={applyCss([
        buttonCss(evaluateButtonColor(buttonColor, colors)),
        variant || (iconName && !label) ? circularBtnCss : extendedBtnCss,
        isDisabled && disabledBtnCss,
      ])}
      className={className}
      data-cy={`button ${dataCy}`}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...rest}
    >
      {iconName && (
        <IconContainer>
          <FunctionButtonIcon iconSize={iconSize} isLoading={isLoading}>
            {iconName}
          </FunctionButtonIcon>
          {isLoading && <FunctionButtonLoader size={loaderSize} />}
        </IconContainer>
      )}
      {iconName && label && <Fragment>&nbsp;</Fragment>}
      {label}
    </button>
  );

  const renderButtonWithTooltip = () => {
    if (!tooltip || disabled) {
      return renderStyledFunctionButton();
    }
    return (
      <Tooltip
        data-cy="icon-tooltip"
        id={tooltip}
        title={tooltip}
        placement="top-start"
      >
        <div css={buttonWrapperCss}>{renderStyledFunctionButton()}</div>
      </Tooltip>
    );
  };

  if (!linkTo) return renderButtonWithTooltip();

  return (
    <Link to={linkTo} css={linkCss}>
      {renderButtonWithTooltip()}
    </Link>
  );
};

type OtherFabProps = Omit<
  FabProps,
  "onClick" | "variant" | "tooltip" | "type" | "children"
>;

export type EwaButton2Props = OtherFabProps & {
  buttonColor?: string | keyof Colors;
  label?: string | null;
  iconName?: IconName;
  variant?: Variant;
  onClick?: ButtonClickCallback | (() => void);
  isLoading?: boolean;
  loaderSize?: number;
  tooltip?: string;
  iconSize?: number;
  type?: ButtonType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkTo?: LinkTo<any>;
  dataCy?: string; //
  appliedFilters?: boolean;
};

export default EwaButton2;
